import {
  CANCEL_APPROVAL_TYPES,
  CONTRACT_APPROVAL_TYPES,
  isDev,
  isLocal,
} from '@/constant';
import { SUBJECT_MAX_LENGTH } from '@/constant/approval';
import type { CancelApprovalAttributes } from '@/types/approval';
import type { CancelTargetSnapshot } from '@/utils/approval/cancelApproval';
import {
  checkExpenseBeforeSave,
  EXPENSE_PRE_APPROVAL_TYPE,
  type ExpensePreApprovalState,
} from '@/utils/approval/expensePreApproval';
import {
  checkOpenConstructionCostBeforeSave,
  OPEN_CONSTRUCTION_COST_SETTLEMENT_TYPE,
  OPEN_CONSTRUCTION_COST_TYPE,
  type OpenConstructionCostState,
} from '@/utils/approval/openConstructionCost';
import {
  checkFncoPreMarketingBeforeSave,
  FNCO_PRE_MARKETING_TYPE,
  type FncoPreMarketingState,
} from '@/utils/approval/fncoPreMarketing';
import {
  checkMarketingExpenseBeforeSave,
  MARKETING_EXPENSE_TYPE,
  type MarketingExpenseState,
} from '@/utils/approval/marketingExpense';
import {
  checkEntExpenseBeforeSave,
  ENT_EXPENSE_TYPE,
  type EntExpenseState,
} from '@/utils/approval/entExpense';
import {
  checkWorkSpecificationBeforeSave,
  WORK_SPECIFICATION_TYPE,
} from '@/utils/approval/workSpecification';
import {
  FNCO_SHOW_ROOM_MISSING_MESSAGE,
  FNCO_SHOW_ROOM_TYPE,
} from '@/utils/approval/fncoShowRoom';
import {
  FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE,
  FNCO_PRODUCT_PURCHASE_TYPES,
} from '@/utils/approval/fncoProductPurchase';
import {
  checkVehicleCostBeforeSave,
  VEHICLE_COST_TYPE,
} from '@/utils/approval/vehicleCost';
import {
  checkResignationBeforeSave,
  RESIGNATION_TYPE,
  type ResignationFormState,
} from '@/utils/approval/resignation';
import {
  checkNewCorporateCardBeforeSave,
  NEW_CORPORATE_CARD_TYPE,
  type NewCorporateCardFormState,
} from '@/utils/approval/newCorporateCard';
import {
  BP_HK_TYPE,
  BP_TW_TYPE,
  checkBpBeforeSave,
  type BpFormState,
} from '@/utils/approval/businessPartnerRegistration';
import {
  checkShopManagerFeeBeforeSave,
  SHOP_MANAGER_FEE_TYPE,
  type ShopManagerFeeState,
} from '@/utils/approval/shopManagerFee';
import {
  checkSapContractFeeBeforeSave,
  SAP_CONTRACT_FEE_TYPE,
  type SapContractFeeState,
} from '@/utils/approval/sapContractFee';
import {
  checkSerpStockAdjustmentBeforeSave,
  SERP_STOCK_ADJUSTMENT_TYPE,
  type SerpStockAdjustmentState,
} from '@/utils/approval/serpStockAdjustment';
import {
  checkSerpStoreChangeBeforeSave,
  SERP_STORE_CHANGE_TYPE,
  type SerpStoreChangeState,
} from '@/utils/approval/serpStoreChange';
import {
  checkSerpStoreOpenBeforeSave,
  SERP_STORE_OPEN_TYPE,
  type SerpStoreOpenState,
} from '@/utils/approval/serpStoreOpen';
import {
  checkSerpStoreReturnBeforeSave,
  SERP_STORE_RETURN_TYPE,
  type SerpStoreReturnState,
} from '@/utils/approval/serpStoreReturn';
import {
  checkSerpLendingDelayBeforeSave,
  SERP_LENDING_DELAY_TYPE,
  type SerpLendingDelayState,
} from '@/utils/approval/serpLendingDelay';
import {
  checkSerpStoreClosureBeforeSave,
  SERP_STORE_CLOSURE_TYPE,
  type SerpStoreClosureState,
} from '@/utils/approval/serpStoreClosure';
import {
  checkSerpIntermediaryReplaceBeforeSave,
  SERP_INTERMEDIARY_REPLACE_TYPE,
  type SerpIntermediaryReplaceState,
} from '@/utils/approval/serpIntermediaryReplace';
import {
  checkSerpSellingPriceBeforeSave,
  SERP_SELLING_PRICE_TYPE,
  type SerpSellingPriceState,
} from '@/utils/approval/serpSellingPrice';
import {
  checkSerpSeasonPlanBeforeSave,
  SERP_SEASON_PLAN_TYPE,
  type SerpSeasonPlanState,
} from '@/utils/approval/serpSeasonPlan';
import {
  checkSerpBulkStockAdjustmentBeforeSave,
  SERP_BULK_STOCK_ADJUSTMENT_TYPE,
  type SerpBulkStockAdjustmentState,
} from '@/utils/approval/serpBulkStockAdjustment';
import {
  checkSerpSeasonEndBeforeSave,
  SERP_SEASON_END_TYPE,
  type SerpSeasonEndState,
} from '@/utils/approval/serpSeasonEnd';
import {
  checkSerpApStockTransferBeforeSave,
  SERP_AP_STOCK_TRANSFER_TYPE,
  type SerpApStockTransferState,
} from '@/utils/approval/serpApStockTransfer';
import {
  checkMortgageFeeBeforeSave,
  MORTGAGE_FEE_TYPE,
  type MortgageFeeFormState,
} from '@/utils/approval/mortgageFee';
import {
  checkStoreOpenBeforeSave,
  STORE_OPEN_TYPE,
  type StoreOpenFormState,
} from '@/utils/approval/storeOpen';
import {
  CARD_LIMIT_INCREASE_TYPE,
  CARD_REISSUE_TYPE,
  checkCorporateCardRequestBeforeSave,
  type CorporateCardRequestFormState,
} from '@/utils/approval/corporateCardRequest';
import {
  ANNUAL_LEAVE_REQUEST_TYPE,
  checkAnnualLeaveRequestBeforeSave,
  type AnnualLeaveRequestFormState,
} from '@/utils/approval/annualLeaveRequest';
import {
  checkLongServiceLeaveBeforeSave,
  FNCO_LONG_SERVICE_LEAVE_TYPE,
  type LongServiceLeaveFormState,
} from '@/utils/approval/longServiceLeave';
import {
  checkProbationEvaluationBeforeSave,
  PROBATION_EVALUATION_TYPE,
  type ProbationEvaluationState,
} from '@/utils/approval/probationEvaluation';
import {
  checkSalesBrandPermissionBeforeSave,
  SALES_BRAND_PERMISSION_TYPE,
  type SalesBrandPermissionState,
} from '@/utils/approval/salesBrandPermission';
import {
  checkGroupSalesBeforeSave,
  GROUP_SALES_TYPE,
  type GroupSalesState,
} from '@/utils/approval/groupSales';
import {
  checkMarketingCaseBeforeSave,
  MARKETING_CASE_TYPE,
  type MarketingCaseState,
} from '@/utils/approval/marketingCase';
import {
  checkStyleMasterPriceBeforeSave,
  STYLE_MASTER_PRICE_TYPE,
  type StyleMasterPriceState,
} from '@/utils/approval/styleMasterPrice';
import {
  checkProductionCostClosingBeforeSave,
  PRODUCTION_COST_CLOSING_TYPE,
  type ProductionCostClosingState,
} from '@/utils/approval/productionCostClosing';
import {
  checkClaimClosingBeforeSave,
  CLAIM_CLOSING_TYPE,
  type ClaimClosingState,
} from '@/utils/approval/claimClosing';
import {
  checkInventoryReplenishmentBeforeSave,
  INVENTORY_REPLENISHMENT_TYPE,
  type InventoryReplenishmentState,
} from '@/utils/approval/inventoryReplenishment';
import {
  checkSalesContractBeforeSave,
  SALES_CONTRACT_TYPE,
  type SalesContractState,
} from '@/utils/approval/salesContract';
import {
  checkStorageMaterialOrderBeforeSave,
  STORAGE_MATERIAL_ORDER_TYPE,
  type StorageMaterialOrderState,
} from '@/utils/approval/storageMaterialOrder';
import {
  checkCreditNoteBeforeSave,
  CREDIT_NOTE_TYPE,
  type CreditNoteState,
} from '@/utils/approval/creditNote';
import {
  checkClaimNoteBeforeSave,
  CLAIM_NOTE_TYPE,
  type ClaimNoteState,
} from '@/utils/approval/claimNote';
import {
  checkSupplierEvaluationBeforeSave,
  SUPPLIER_EVALUATION_TYPE,
  type SupplierEvaluationState,
} from '@/utils/approval/supplierEvaluation';
import {
  checkProductionVendorBeforeSave,
  PRODUCTION_VENDOR_TYPE,
  type ProductionVendorState,
} from '@/utils/approval/productionVendor';
import {
  checkProductLifecycleBeforeSave,
  PRODUCT_LIFECYCLE_TYPE,
  type ProductLifecycleState,
} from '@/utils/approval/productLifecycle';
import {
  checkPurchaseRequisitionBeforeSave,
  PO_CHANGE_TYPE,
  PURCHASE_REQUISITION_TYPE,
  type PurchaseRequisitionState,
} from '@/utils/approval/purchaseRequisition';
import {
  checkCostQuotationBeforeSave,
  COST_QUOTATION_TYPE,
  type CostQuotationState,
} from '@/utils/approval/costQuotation';
import {
  checkGroupSalesMultiBeforeSubmit,
  GROUP_SALES_MULTI_TYPE,
  type GroupSalesMultiState,
} from '@/utils/approval/groupSalesMulti';
import {
  checkSapBudgetIncreaseBeforeSave,
  SAP_BUDGET_INCREASE_TYPE,
  type SapBudgetIncreaseState,
} from '@/utils/approval/sapBudgetIncrease';
import {
  checkSapCollateralChangeBeforeSave,
  SAP_COLLATERAL_CHANGE_TYPE,
  SAP_COLLATERAL_CREATION_TYPE,
  type SapCollateralChangeState,
} from '@/utils/approval/sapCollateralChange';
import {
  checkInfluencerPplBeforeSave,
  INFLUENCER_PPL_TYPE,
  type InfluencerPplState,
} from '@/utils/approval/influencerPpl';
import {
  checkInfluencerMonthlyReportBeforeSave,
  INFLUENCER_MONTHLY_REPORT_TYPE,
  type InfluencerMonthlyReportState,
} from '@/utils/approval/influencerMonthlyReport';
import {
  checkProductionCostBeforeSave,
  PRODUCTION_COST_TYPE,
  type ProductionCostState,
} from '@/utils/approval/productionCost';
import {
  checkExternalLoanBeforeSave,
  EXTERNAL_LOAN_TYPE,
  type ExternalLoanState,
} from '@/utils/approval/externalLoan';
import {
  checkSapBondStatusBeforeSave,
  SAP_BOND_STATUS_TYPE,
  type SapBondStatusState,
} from '@/utils/approval/sapBondStatus';
import {
  checkSaTravelExpenseBeforeSave,
  SA_TRAVEL_EXPENSE_TYPE,
  SA_TRAVEL_REQUEST_TYPE,
  type SaTravelExpenseState,
} from '@/utils/approval/saTravelExpense';
import {
  findExpenseVoucherViolation,
  type ExpenseVoucherState,
} from '@/utils/approval/expenseVoucher';
import {
  checkFncoPriceSupplyBeforeSave,
  FNCO_PRICE_DISCOUNT_TYPE,
  FNCO_PRICE_SALE_TYPE,
  FNCO_PRICE_SUPPLY_TYPE,
  type FncoPriceSupplyState,
} from '@/utils/approval/fncoPriceSupply';
import {
  checkDiscountVoucherBeforeSave,
  DISCOUNT_VOUCHER_TYPE,
  type DiscountVoucherState,
} from '@/utils/approval/discountVoucher';
import type { MemberOrganization } from '@/utils/approval/passportMemberStatus';
import {
  ACCOUNTING_BP_TYPE,
  checkAccountingBpBeforeSave,
  isAccountingBpRegister,
  withAccountingBpRequestType,
  type AccountingBpState,
} from '@/utils/approval/accountingBusinessPartner';

/** 작성 화면·조회 화면 상신이 각자 만들어 넘기는 검사 입력 */
export type SubmitCheckContext = {
  apvType: number;
  subject: string;
  /** 결재자 + 협조결재자 passportId. 기안자 제외 */
  lineMemberIds: string[];
  contractIds: number[];
  cancelTarget: CancelTargetSnapshot | CancelApprovalAttributes | null;
  /** SA 전표(99) 묶음번호. 전표 라인이 확인되지 않았으면 null */
  sapDocumentNo: string | null;
  coApproverIds: string[];
  /** 조회 중·실패면 null */
  memberOrganizations: ReadonlyMap<string, MemberOrganization | null> | null;
  /** 사전 비용(205) 작성 폼 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  expensePreApproval?: ExpensePreApprovalState | null;
  /** 매장 OPEN(233) 작성 폼 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  openConstructionCost?: OpenConstructionCostState | null;
  /** F&CO 마케팅 사전(221) 작성 폼 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  fncoPreMarketing?: FncoPreMarketingState | null;
  /** 마케팅 비용(212) 작성 폼 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  marketingExpense?: MarketingExpenseState | null;
  /** ENT. 프로젝트(비용)(213) 작성 폼 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  entExpense?: EntExpenseState | null;
  /** 작업사양서(183) 체크 항목. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  workSpecChecklist?: string[] | null;
  /** F&CO 쇼룸 출고(209) 요청번호. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  fncoShowRoomRequestNumber?: string | null;
  /** F&CO 신제품 발주(202)·운영제품 재발주(203) 요청번호. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  fncoPurchaseRequestNumber?: string | null;
  /** 차량유지비 신청(93) 임직원 정보 HTML. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  vehicleCostContent?: string | null;
  /** 퇴직원(196) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  resignation?: ResignationFormState | null;
  /** 신규입사자 법인카드(204) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  newCorporateCard?: NewCorporateCardFormState | null;
  /** 거래처 신규/변경 HK·TW(200·201) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  bpRegistration?: BpFormState | null;
  /** SAP 중간 관리 수수료 정산(157) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  shopManagerFee?: ShopManagerFeeState | null;
  /** SAP 도급비 수수료 정산(167) 조회 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  sapContractFee?: SapContractFeeState | null;
  /** SERP 재고실사 조정(169) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpStockAdjustment?: SerpStockAdjustmentState | null;
  /** SERP 매장 변경(162) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpStoreChange?: SerpStoreChangeState | null;
  /** SERP 매장 개점(161) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpStoreOpen?: SerpStoreOpenState | null;
  /** SERP 매장반품 요청(171) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpStoreReturn?: SerpStoreReturnState | null;
  /** SERP 여신유예신청(163) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpLendingDelay?: SerpLendingDelayState | null;
  /** SERP 폐점 정산(181) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpStoreClosure?: SerpStoreClosureState | null;
  /** SERP 중간관리자 교체 정산(182) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpIntermediaryReplace?: SerpIntermediaryReplaceState | null;
  /** SERP 판매가 조정(160) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpSellingPrice?: SerpSellingPriceState | null;
  /** SERP 시즌운영계획(159) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpSeasonPlan?: SerpSeasonPlanState | null;
  /** SERP 재고실사 조정(다중)(210) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpBulkStockAdjustment?: SerpBulkStockAdjustmentState | null;
  /** SERP 시즌마감 조정(174) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpSeasonEnd?: SerpSeasonEndState | null;
  /** SERP AP 재고 이관 요청(170) 조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  serpApStockTransfer?: SerpApStockTransferState | null;
  /** (구)근저당설정비용 지급(81) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  mortgageFee?: MortgageFeeFormState | null;
  /** (구)매장 OPEN(8) 양식·매장. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  storeOpen?: StoreOpenFormState | null;
  /** 법인카드 한도증액·재발급(206·207) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  corporateCardRequest?: CorporateCardRequestFormState | null;
  /** 연차휴가(32) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  annualLeaveRequest?: AnnualLeaveRequestFormState | null;
  /** F&CO 장기근속 포상휴가(240) 입력값. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  longServiceLeave?: LongServiceLeaveFormState | null;
  /** 수습평가서(188) 확인한 대상자. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  probationEvaluation?: ProbationEvaluationState | null;
  /** Sales 브랜드 권한 변경(215) 체크 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  salesBrandPermission?: SalesBrandPermissionState | null;
  /** 단체판매 진행(61) 확정 건. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  groupSales?: GroupSalesState | null;
  marketingCase?: MarketingCaseState | null;
  productionVendor?: ProductionVendorState | null;
  productLifecycle?: ProductLifecycleState | null;
  styleMasterPrice?: StyleMasterPriceState | null;
  productionCostClosing?: ProductionCostClosingState | null;
  claimClosing?: ClaimClosingState | null;
  inventoryReplenishment?: InventoryReplenishmentState | null;
  storageMaterialOrder?: StorageMaterialOrderState | null;
  supplierEvaluation?: SupplierEvaluationState | null;
  purchaseRequisition?: PurchaseRequisitionState | null;
  costQuotation?: CostQuotationState | null;
  salesContract?: SalesContractState | null;
  creditNote?: CreditNoteState | null;
  claimNote?: ClaimNoteState | null;
  /** 단체판매 진행(다중)(131) 확정 건들. 조회 화면 상신은 저장된 attributes 로 — 조회 실패 건은 임시저장이 허용돼 상신 때 막아야 함 */
  groupSalesMulti?: GroupSalesMultiState | null;
  /** SAP 예산증액 신청(187) 선택 계정·금액. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  sapBudgetIncrease?: SapBudgetIncreaseState | null;
  /** SAP 담보 신규(154)·변경(155) sapKey·조회 상태. 조회 화면 상신은 임시저장이 없어 미전달 */
  sapCollateralChange?: SapCollateralChangeState | null;
  /** 회계거래처(101) 묶음번호·거래처 표. 업체소싱정보는 bpRegistration. 조회 화면 상신은 임시저장이 없어 미전달 */
  accountingBp?: AccountingBpState | null;
  /** 인플루언서 PPL 정산(135) 일련번호·리포트·목적. 조회 화면 상신은 임시저장이 없어 미전달 */
  influencerPpl?: InfluencerPplState | null;
  /** 인플루언서 월마감 보고서(108) 일련번호·조회 상태. 조회 화면 상신은 임시저장이 없어 미전달 */
  influencerMonthlyReport?: InfluencerMonthlyReportState | null;
  /** 생산비 마감(회계)(216) 마감 월·조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  productionCost?: ProductionCostState | null;
  /** 외담대 발행(217) 발급일·조회 결과. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  externalLoan?: ExternalLoanState | null;
  /** SAP 채권현황(185) 조회 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  sapBondStatus?: SapBondStatusState | null;
  /** SA 신규 출장비 정산(177)·사전 신청(179) SAP 데이터. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  saTravelExpense?: SaTravelExpenseState | null;
  /** 가격 등록 공급가·할인율·판매가 요청(197~199) 요청번호·조회 상태. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  fncoPriceSupply?: FncoPriceSupplyState | null;
  /** 할인권 발행(68) 확정 할인권. 조회 화면 상신은 저장 시 검사를 거쳐 미전달 */
  discountVoucher?: DiscountVoucherState | null;
  /** 지출결의서(111) 작성 폼 상태. 조회 화면 상신은 미전달 */
  expenseVoucher?: ExpenseVoucherState | null;
};

/** 위반이면 번역 key(한국어 문구), 통과면 null */
export type SubmitCheck = (ctx: SubmitCheckContext) => string | null;

export const APPROVAL_LINE_SHORTAGE_MESSAGE =
  '결재라인을 추가하시기 바랍니다. 1인 결재는 허용되지 않습니다.';

/** 상신·임시저장 공통 — 임시저장도 서버가 같은 기준으로 422 */
export const checkApprovalLineShortage = (
  lineMemberIds: string[],
): string | null =>
  lineMemberIds.length > 0 ? null : APPROVAL_LINE_SHORTAGE_MESSAGE;

/** 상신·임시저장 공통 — 임시저장도 서버가 빈 제목을 422 */
export const checkSubjectRequired = (subject: string): string | null =>
  subject.trim().length === 0 ? '품의 제목을 입력해주세요.' : null;

const subjectRequired: SubmitCheck = ({ subject }) =>
  checkSubjectRequired(subject);

const subjectMaxLength: SubmitCheck = ({ subject }) =>
  subject.trim().length > SUBJECT_MAX_LENGTH
    ? '제목은 200자 이내로 작성해주세요.'
    : null;

const approvalLineRequired: SubmitCheck = ({ lineMemberIds }) =>
  checkApprovalLineShortage(lineMemberIds);

const contractLinkRequired: SubmitCheck = ({ contractIds }) =>
  contractIds.length === 0 ? '계약을 1건 이상 연결해 주세요.' : null;

const cancelTargetRequired: SubmitCheck = ({ cancelTarget }) =>
  cancelTarget?.approvalId
    ? null
    : '취소할 품의가 확인되지 않아 상신할 수 없습니다.';

const expensePreApprovalRequired: SubmitCheck = ({ expensePreApproval }) =>
  expensePreApproval === undefined
    ? null
    : checkExpenseBeforeSave(expensePreApproval);

const openConstructionCostRequired: SubmitCheck = ({ openConstructionCost }) =>
  openConstructionCost === undefined
    ? null
    : checkOpenConstructionCostBeforeSave(openConstructionCost);

const fncoPreMarketingRequired: SubmitCheck = ({ fncoPreMarketing }) =>
  fncoPreMarketing === undefined
    ? null
    : checkFncoPreMarketingBeforeSave(fncoPreMarketing);

const marketingExpenseRequired: SubmitCheck = ({ marketingExpense }) =>
  marketingExpense === undefined
    ? null
    : checkMarketingExpenseBeforeSave(marketingExpense);

const entExpenseRequired: SubmitCheck = ({ entExpense }) =>
  entExpense === undefined ? null : checkEntExpenseBeforeSave(entExpense);

const workSpecChecklistRequired: SubmitCheck = ({ workSpecChecklist }) =>
  workSpecChecklist === undefined
    ? null
    : checkWorkSpecificationBeforeSave(workSpecChecklist);

const fncoShowRoomRequestNumberRequired: SubmitCheck = ({
  fncoShowRoomRequestNumber,
}) =>
  fncoShowRoomRequestNumber === undefined || fncoShowRoomRequestNumber
    ? null
    : FNCO_SHOW_ROOM_MISSING_MESSAGE;

const fncoPurchaseRequestNumberRequired: SubmitCheck = ({
  fncoPurchaseRequestNumber,
}) =>
  fncoPurchaseRequestNumber === undefined || fncoPurchaseRequestNumber
    ? null
    : FNCO_PRODUCT_PURCHASE_MISSING_MESSAGE;

const vehicleCostContentRequired: SubmitCheck = ({ vehicleCostContent }) =>
  vehicleCostContent === undefined
    ? null
    : checkVehicleCostBeforeSave(vehicleCostContent);

const resignationRequired: SubmitCheck = ({ resignation }) =>
  resignation === undefined ? null : checkResignationBeforeSave(resignation);

const newCorporateCardRequired: SubmitCheck = ({ newCorporateCard }) =>
  newCorporateCard === undefined
    ? null
    : checkNewCorporateCardBeforeSave(newCorporateCard);

const bpRegistrationRequired: SubmitCheck = ({ bpRegistration }) =>
  bpRegistration === undefined ? null : checkBpBeforeSave(bpRegistration);
const shopManagerFeeRequired: SubmitCheck = ({ shopManagerFee }) =>
  shopManagerFee === undefined
    ? null
    : checkShopManagerFeeBeforeSave(shopManagerFee);
const sapContractFeeRequired: SubmitCheck = ({ sapContractFee }) =>
  sapContractFee === undefined
    ? null
    : checkSapContractFeeBeforeSave(sapContractFee);
const mortgageFeeRequired: SubmitCheck = ({ mortgageFee }) =>
  mortgageFee === undefined ? null : checkMortgageFeeBeforeSave(mortgageFee);
const storeOpenRequired: SubmitCheck = ({ storeOpen }) =>
  storeOpen === undefined ? null : checkStoreOpenBeforeSave(storeOpen);
const corporateCardRequestRequired: SubmitCheck = ({
  apvType,
  corporateCardRequest,
}) =>
  corporateCardRequest === undefined
    ? null
    : checkCorporateCardRequestBeforeSave(apvType, corporateCardRequest);
const annualLeaveRequestRequired: SubmitCheck = ({ annualLeaveRequest }) =>
  annualLeaveRequest === undefined
    ? null
    : checkAnnualLeaveRequestBeforeSave(annualLeaveRequest);
const longServiceLeaveRequired: SubmitCheck = ({ longServiceLeave }) =>
  longServiceLeave === undefined
    ? null
    : checkLongServiceLeaveBeforeSave(longServiceLeave);
const probationEvaluationRequired: SubmitCheck = ({ probationEvaluation }) =>
  probationEvaluation === undefined
    ? null
    : checkProbationEvaluationBeforeSave(probationEvaluation);
const salesBrandPermissionRequired: SubmitCheck = ({ salesBrandPermission }) =>
  salesBrandPermission === undefined
    ? null
    : checkSalesBrandPermissionBeforeSave(salesBrandPermission);
const groupSalesRequired: SubmitCheck = ({ groupSales }) =>
  groupSales === undefined ? null : checkGroupSalesBeforeSave(groupSales);
const marketingCaseRequired: SubmitCheck = ({ marketingCase }) =>
  marketingCase === undefined
    ? null
    : checkMarketingCaseBeforeSave(marketingCase);
const groupSalesMultiRequired: SubmitCheck = ({ groupSalesMulti }) =>
  groupSalesMulti === undefined
    ? null
    : checkGroupSalesMultiBeforeSubmit(groupSalesMulti);
const sapBudgetIncreaseRequired: SubmitCheck = ({ sapBudgetIncrease }) =>
  sapBudgetIncrease === undefined
    ? null
    : checkSapBudgetIncreaseBeforeSave(sapBudgetIncrease);
const sapCollateralChangeRequired: SubmitCheck = ({ sapCollateralChange }) =>
  sapCollateralChange === undefined
    ? null
    : checkSapCollateralChangeBeforeSave(sapCollateralChange);
const influencerMonthlyReportRequired: SubmitCheck = ({
  influencerMonthlyReport,
}) =>
  influencerMonthlyReport === undefined
    ? null
    : checkInfluencerMonthlyReportBeforeSave(influencerMonthlyReport);
const productionCostRequired: SubmitCheck = ({ productionCost }) =>
  productionCost === undefined
    ? null
    : checkProductionCostBeforeSave(productionCost);
const externalLoanRequired: SubmitCheck = ({ externalLoan }) =>
  externalLoan === undefined ? null : checkExternalLoanBeforeSave(externalLoan);
const sapBondStatusRequired: SubmitCheck = ({ sapBondStatus }) =>
  sapBondStatus === undefined
    ? null
    : checkSapBondStatusBeforeSave(sapBondStatus);
const saTravelExpenseRequired: SubmitCheck = ({ saTravelExpense }) =>
  saTravelExpense === undefined
    ? null
    : checkSaTravelExpenseBeforeSave(saTravelExpense);
const expenseVoucherRequired: SubmitCheck = ({ expenseVoucher }) =>
  expenseVoucher === undefined
    ? null
    : findExpenseVoucherViolation(expenseVoucher)?.message ?? null;
const discountVoucherRequired: SubmitCheck = ({ discountVoucher }) =>
  discountVoucher === undefined
    ? null
    : checkDiscountVoucherBeforeSave(discountVoucher);

const serpStockAdjustmentRequired: SubmitCheck = ({ serpStockAdjustment }) =>
  serpStockAdjustment === undefined
    ? null
    : checkSerpStockAdjustmentBeforeSave(serpStockAdjustment);

const serpStoreChangeRequired: SubmitCheck = ({ serpStoreChange }) =>
  serpStoreChange === undefined
    ? null
    : checkSerpStoreChangeBeforeSave(serpStoreChange);

const serpStoreOpenRequired: SubmitCheck = ({ serpStoreOpen }) =>
  serpStoreOpen === undefined
    ? null
    : checkSerpStoreOpenBeforeSave(serpStoreOpen);

const serpStoreReturnRequired: SubmitCheck = ({ serpStoreReturn }) =>
  serpStoreReturn === undefined
    ? null
    : checkSerpStoreReturnBeforeSave(serpStoreReturn);

const serpLendingDelayRequired: SubmitCheck = ({ serpLendingDelay }) =>
  serpLendingDelay === undefined
    ? null
    : checkSerpLendingDelayBeforeSave(serpLendingDelay);

const serpStoreClosureRequired: SubmitCheck = ({ serpStoreClosure }) =>
  serpStoreClosure === undefined
    ? null
    : checkSerpStoreClosureBeforeSave(serpStoreClosure);

const serpIntermediaryReplaceRequired: SubmitCheck = ({
  serpIntermediaryReplace,
}) =>
  serpIntermediaryReplace === undefined
    ? null
    : checkSerpIntermediaryReplaceBeforeSave(serpIntermediaryReplace);

const serpSellingPriceRequired: SubmitCheck = ({ serpSellingPrice }) =>
  serpSellingPrice === undefined
    ? null
    : checkSerpSellingPriceBeforeSave(serpSellingPrice);

const serpSeasonPlanRequired: SubmitCheck = ({ serpSeasonPlan }) =>
  serpSeasonPlan === undefined
    ? null
    : checkSerpSeasonPlanBeforeSave(serpSeasonPlan);
const serpBulkStockAdjustmentRequired: SubmitCheck = ({
  serpBulkStockAdjustment,
}) =>
  serpBulkStockAdjustment === undefined
    ? null
    : checkSerpBulkStockAdjustmentBeforeSave(serpBulkStockAdjustment);
const serpSeasonEndRequired: SubmitCheck = ({ serpSeasonEnd }) =>
  serpSeasonEnd === undefined
    ? null
    : checkSerpSeasonEndBeforeSave(serpSeasonEnd);
const serpApStockTransferRequired: SubmitCheck = ({ serpApStockTransfer }) =>
  serpApStockTransfer === undefined
    ? null
    : checkSerpApStockTransferBeforeSave(serpApStockTransfer);
const fncoPriceSupplyRequired: SubmitCheck = ({ fncoPriceSupply }) =>
  fncoPriceSupply === undefined
    ? null
    : checkFncoPriceSupplyBeforeSave(fncoPriceSupply);
const productionVendorRequired: SubmitCheck = ({ productionVendor }) =>
  productionVendor === undefined
    ? null
    : checkProductionVendorBeforeSave(productionVendor);
const productLifecycleRequired: SubmitCheck = ({ productLifecycle }) =>
  productLifecycle === undefined
    ? null
    : checkProductLifecycleBeforeSave(productLifecycle);
const styleMasterPriceRequired: SubmitCheck = ({ styleMasterPrice }) =>
  styleMasterPrice === undefined
    ? null
    : checkStyleMasterPriceBeforeSave(styleMasterPrice);
const claimClosingRequired: SubmitCheck = ({ claimClosing }) =>
  claimClosing === undefined ? null : checkClaimClosingBeforeSave(claimClosing);
const productionCostClosingRequired: SubmitCheck = ({
  productionCostClosing,
}) =>
  productionCostClosing === undefined
    ? null
    : checkProductionCostClosingBeforeSave(productionCostClosing);
const inventoryReplenishmentRequired: SubmitCheck = ({
  inventoryReplenishment,
}) =>
  inventoryReplenishment === undefined
    ? null
    : checkInventoryReplenishmentBeforeSave(inventoryReplenishment);
const storageMaterialOrderRequired: SubmitCheck = ({ storageMaterialOrder }) =>
  storageMaterialOrder === undefined
    ? null
    : checkStorageMaterialOrderBeforeSave(storageMaterialOrder);
const supplierEvaluationRequired: SubmitCheck = ({ supplierEvaluation }) =>
  supplierEvaluation === undefined
    ? null
    : checkSupplierEvaluationBeforeSave(supplierEvaluation);
const purchaseRequisitionRequired: SubmitCheck = ({ purchaseRequisition }) =>
  purchaseRequisition === undefined
    ? null
    : checkPurchaseRequisitionBeforeSave(purchaseRequisition);
const costQuotationRequired: SubmitCheck = ({ costQuotation }) =>
  costQuotation === undefined
    ? null
    : checkCostQuotationBeforeSave(costQuotation);
const salesContractRequired: SubmitCheck = ({ salesContract }) =>
  salesContract === undefined
    ? null
    : checkSalesContractBeforeSave(salesContract);
const creditNoteRequired: SubmitCheck = ({ creditNote }) =>
  creditNote === undefined ? null : checkCreditNoteBeforeSave(creditNote);
const claimNoteRequired: SubmitCheck = ({ claimNote }) =>
  claimNote === undefined ? null : checkClaimNoteBeforeSave(claimNote);

const influencerPplRequired: SubmitCheck = ({ influencerPpl }) =>
  influencerPpl === undefined
    ? null
    : checkInfluencerPplBeforeSave(influencerPpl);

/** 레거시 save_doc_eas_bp 순서 — 거래처 표, 마지막 행이 '생성'이면 업체소싱정보 */
const accountingBpRequired: SubmitCheck = ({
  accountingBp,
  bpRegistration,
}) => {
  if (accountingBp === undefined) return null;
  const tableMessage = checkAccountingBpBeforeSave(accountingBp);
  if (tableMessage || !accountingBp) return tableMessage;
  if (!isAccountingBpRegister(accountingBp.partners)) return null;
  return checkBpBeforeSave(
    bpRegistration
      ? withAccountingBpRequestType(bpRegistration, accountingBp.partners)
      : null,
  );
};

const saVoucherRequired: SubmitCheck = ({ sapDocumentNo }) =>
  sapDocumentNo ? null : 'SA 전표 정보 로드에 실패하였습니다.';

/** 작성자 법인과 무관하게 F&F 회계팀만 인정 */
export const FNF_ACCOUNTING_TEAM: MemberOrganization = {
  divisionCode: 'A10',
  departmentCode: '1037',
};

const isSameOrganization = (
  a: MemberOrganization | null | undefined,
  b: MemberOrganization,
): boolean =>
  a?.divisionCode === b.divisionCode && a.departmentCode === b.departmentCode;

const intranetIdOf = (member: MemberOrganization | null | undefined) =>
  (member?.intranetId ?? member?.email?.split('@')[0])?.toLowerCase();

/** 협조결재자 중 지정 조직 소속(또는 지정 인트라넷 ID) 1명 이상 필수. 겸직 소속은 불인정 */
export const requireCoApproverFrom =
  (
    organizations: MemberOrganization[],
    message: string,
    intranetIds: string[] = [],
  ): SubmitCheck =>
  ({ coApproverIds, memberOrganizations }) => {
    if (!memberOrganizations) {
      return '결재라인 정보를 확인하는 중입니다. 잠시 후 다시 시도해주세요.';
    }
    return coApproverIds.some((id) => {
      const member = memberOrganizations.get(id);
      return (
        organizations.some((org) => isSameOrganization(member, org)) ||
        intranetIds.includes(intranetIdOf(member) ?? '')
      );
    })
      ? null
      : message;
  };

/** 수입결의서(112) 협조결재자 허용 조직 — 재무회계·재무·경영관리·파트너스 관리·홀딩스 재무 */
export const VOUCHER_INCOMING_CO_APPROVER_TEAMS: MemberOrganization[] = [
  { divisionCode: 'A10', departmentCode: '1011' },
  FNF_ACCOUNTING_TEAM,
  { divisionCode: 'A10', departmentCode: '1038' },
  { divisionCode: 'A81', departmentCode: 'C120' },
  { divisionCode: 'HC10', departmentCode: 'HC20' },
];

/** SA 전표(99) 협조결재자 허용 조직 — 112 와 같은 목록 + FE10 FE90 */
export const SA_VOUCHER_CO_APPROVER_TEAMS: MemberOrganization[] = [
  ...VOUCHER_INCOMING_CO_APPROVER_TEAMS,
  { divisionCode: 'FE10', departmentCode: 'FE90' },
];

/** 해외출장 사후품의(158) 협조결재자 허용 조직 — 회계팀·경영관리팀 */
const BUSINESS_TRIP_CO_APPROVER_TEAMS: MemberOrganization[] = [
  FNF_ACCOUNTING_TEAM,
  { divisionCode: 'A10', departmentCode: '1038' },
];

/** 예산증액(187) 협조결재자 허용 조직 — 경영관리팀 */
export const BUDGET_INCREASE_CO_APPROVER_TEAMS: MemberOrganization[] = [
  { divisionCode: 'A10', departmentCode: '1038' },
];

const singleCoApproverRequired: SubmitCheck = ({ coApproverIds }) =>
  coApproverIds.length === 1 ? null : '협조결재자 1인은 필수 사항 입니다.';

/** 협조결재자 지정 부서 제한 — 상신 검사와 결재라인에서 고를 때 검사가 같이 쓴다 */
export type CoApproverRestriction = {
  teams: MemberOrganization[];
  /** 소속과 무관하게 허용하는 인트라넷 ID */
  intranetIds?: string[];
  /** 고를 때 허용 부서가 아닐 때 */
  teamMessage: string;
  /** 상신 때 허용 부서가 없을 때. 없으면 teamMessage */
  submitTeamMessage?: string;
  /** 1명 제한 양식만. 고를 때 2명째를 넣었을 때 문구, 상신 때 인원 문구는 singleCoApproverRequired */
  limitMessage?: string;
};

const CO_APPROVER_TEAM_MESSAGE =
  '협조결재자는 경영관리·회계팀만 지정 가능합니다.';
const SA_CO_APPROVER_LIMIT_MESSAGE = 'SA 협조결재자는 1명만 지정 가능합니다.';
const CO_APPROVER_LIMIT_MESSAGE = '협조결재자는 1명만 지정 가능합니다.';

export const CO_APPROVER_RESTRICTIONS: Partial<
  Record<number, CoApproverRestriction>
> = {
  99: {
    teams: SA_VOUCHER_CO_APPROVER_TEAMS,
    teamMessage: CO_APPROVER_TEAM_MESSAGE,
    limitMessage: SA_CO_APPROVER_LIMIT_MESSAGE,
  },
  [ACCOUNTING_BP_TYPE]: {
    teams: VOUCHER_INCOMING_CO_APPROVER_TEAMS,
    intranetIds: ['jdew'],
    teamMessage: CO_APPROVER_TEAM_MESSAGE,
    limitMessage: SA_CO_APPROVER_LIMIT_MESSAGE,
  },
  111: {
    teams: VOUCHER_INCOMING_CO_APPROVER_TEAMS,
    teamMessage: CO_APPROVER_TEAM_MESSAGE,
    limitMessage: CO_APPROVER_LIMIT_MESSAGE,
  },
  112: {
    teams: VOUCHER_INCOMING_CO_APPROVER_TEAMS,
    teamMessage: CO_APPROVER_TEAM_MESSAGE,
    limitMessage: CO_APPROVER_LIMIT_MESSAGE,
  },
  158: {
    teams: BUSINESS_TRIP_CO_APPROVER_TEAMS,
    teamMessage: CO_APPROVER_TEAM_MESSAGE,
    submitTeamMessage: '협조결재자에 회계팀 담당자 1명은 필수 포함입니다.',
  },
  [SAP_BUDGET_INCREASE_TYPE]: {
    teams: BUDGET_INCREASE_CO_APPROVER_TEAMS,
    teamMessage: '협조결재자는 경영관리팀 팀장만 지정 가능합니다.',
    limitMessage: '예산증액 품의 협조결재자는 1명만 지정 가능합니다.',
  },
};

/** dev·로컬 테스트 계정(주선현·최재영·박민지) — 운영에서는 비움 */
const TEST_CO_APPROVER_INTRANET_IDS =
  isLocal || isDev ? ['sunh', 'choijy8816', 'minmean13'] : [];

const allowedIntranetIdsOf = (restriction: CoApproverRestriction) => [
  ...(restriction.intranetIds ?? []),
  ...TEST_CO_APPROVER_INTRANET_IDS,
];

const isAllowedCoApprover = (
  restriction: CoApproverRestriction,
  member: MemberOrganization | null | undefined,
) =>
  restriction.teams.some((org) => isSameOrganization(member, org)) ||
  allowedIntranetIdsOf(restriction).includes(intranetIdOf(member) ?? '');

/** 고를 때 검사 — 1명 제한인데 이미 있으면 limitMessage, 허용 부서가 아니면 teamMessage. 소속을 모르면 상신 검사에 맡김 */
export const findCoApproverPickViolation = (
  restriction: CoApproverRestriction,
  organization: MemberOrganization | null,
  otherCoApproverCount: number,
): string | null => {
  if (restriction.limitMessage && otherCoApproverCount >= 1) {
    return restriction.limitMessage;
  }
  if (!organization) return null;
  return isAllowedCoApprover(restriction, organization)
    ? null
    : restriction.teamMessage;
};

const coApproverRestrictionChecks = (apvType: number): SubmitCheck[] => {
  const restriction = CO_APPROVER_RESTRICTIONS[apvType];
  if (!restriction) return [];
  return [
    ...(restriction.limitMessage ? [singleCoApproverRequired] : []),
    requireCoApproverFrom(
      restriction.teams,
      restriction.submitTeamMessage ?? restriction.teamMessage,
      allowedIntranetIdsOf(restriction),
    ),
  ];
};

const COMMON_SUBMIT_CHECKS: SubmitCheck[] = [
  subjectRequired,
  subjectMaxLength,
  approvalLineRequired,
];

const forTypes = (
  types: number[],
  checks: SubmitCheck[],
): Record<number, SubmitCheck[]> =>
  Object.fromEntries(types.map((type) => [type, checks]));

const TYPE_SUBMIT_CHECKS: Partial<Record<number, SubmitCheck[]>> = {
  ...forTypes(CONTRACT_APPROVAL_TYPES, [contractLinkRequired]),
  ...forTypes(CANCEL_APPROVAL_TYPES, [cancelTargetRequired]),
  158: coApproverRestrictionChecks(158),
  // 레거시 순서 — 협조결재자 1인 확인 다음 양식 필수값
  111: [...coApproverRestrictionChecks(111), expenseVoucherRequired],
  112: coApproverRestrictionChecks(112),
  [EXPENSE_PRE_APPROVAL_TYPE]: [expensePreApprovalRequired],
  [OPEN_CONSTRUCTION_COST_TYPE]: [openConstructionCostRequired],
  [OPEN_CONSTRUCTION_COST_SETTLEMENT_TYPE]: [openConstructionCostRequired],
  [FNCO_PRE_MARKETING_TYPE]: [fncoPreMarketingRequired],
  [MARKETING_EXPENSE_TYPE]: [marketingExpenseRequired],
  [ENT_EXPENSE_TYPE]: [entExpenseRequired],
  [WORK_SPECIFICATION_TYPE]: [workSpecChecklistRequired],
  [FNCO_SHOW_ROOM_TYPE]: [fncoShowRoomRequestNumberRequired],
  ...forTypes(FNCO_PRODUCT_PURCHASE_TYPES, [fncoPurchaseRequestNumberRequired]),
  [VEHICLE_COST_TYPE]: [vehicleCostContentRequired],
  [RESIGNATION_TYPE]: [resignationRequired],
  [NEW_CORPORATE_CARD_TYPE]: [newCorporateCardRequired],
  [BP_HK_TYPE]: [bpRegistrationRequired],
  [BP_TW_TYPE]: [bpRegistrationRequired],
  [SHOP_MANAGER_FEE_TYPE]: [shopManagerFeeRequired],
  [SAP_CONTRACT_FEE_TYPE]: [sapContractFeeRequired],
  [SERP_STOCK_ADJUSTMENT_TYPE]: [serpStockAdjustmentRequired],
  [SERP_STORE_CHANGE_TYPE]: [serpStoreChangeRequired],
  [SERP_STORE_OPEN_TYPE]: [serpStoreOpenRequired],
  [SERP_STORE_RETURN_TYPE]: [serpStoreReturnRequired],
  [SERP_LENDING_DELAY_TYPE]: [serpLendingDelayRequired],
  [SERP_STORE_CLOSURE_TYPE]: [serpStoreClosureRequired],
  [SERP_INTERMEDIARY_REPLACE_TYPE]: [serpIntermediaryReplaceRequired],
  [SERP_SELLING_PRICE_TYPE]: [serpSellingPriceRequired],
  [SERP_SEASON_PLAN_TYPE]: [serpSeasonPlanRequired],
  [SERP_BULK_STOCK_ADJUSTMENT_TYPE]: [serpBulkStockAdjustmentRequired],
  [SERP_SEASON_END_TYPE]: [serpSeasonEndRequired],
  [SERP_AP_STOCK_TRANSFER_TYPE]: [serpApStockTransferRequired],
  [MORTGAGE_FEE_TYPE]: [mortgageFeeRequired],
  [STORE_OPEN_TYPE]: [storeOpenRequired],
  [CARD_LIMIT_INCREASE_TYPE]: [corporateCardRequestRequired],
  [CARD_REISSUE_TYPE]: [corporateCardRequestRequired],
  [ANNUAL_LEAVE_REQUEST_TYPE]: [annualLeaveRequestRequired],
  [FNCO_LONG_SERVICE_LEAVE_TYPE]: [longServiceLeaveRequired],
  [PROBATION_EVALUATION_TYPE]: [probationEvaluationRequired],
  [SALES_BRAND_PERMISSION_TYPE]: [salesBrandPermissionRequired],
  [GROUP_SALES_TYPE]: [groupSalesRequired],
  [MARKETING_CASE_TYPE]: [marketingCaseRequired],
  [PRODUCTION_VENDOR_TYPE]: [productionVendorRequired],
  [PRODUCT_LIFECYCLE_TYPE]: [productLifecycleRequired],
  [STYLE_MASTER_PRICE_TYPE]: [styleMasterPriceRequired],
  [PRODUCTION_COST_CLOSING_TYPE]: [productionCostClosingRequired],
  [CLAIM_CLOSING_TYPE]: [claimClosingRequired],
  [INVENTORY_REPLENISHMENT_TYPE]: [inventoryReplenishmentRequired],
  [STORAGE_MATERIAL_ORDER_TYPE]: [storageMaterialOrderRequired],
  [SUPPLIER_EVALUATION_TYPE]: [supplierEvaluationRequired],
  [PURCHASE_REQUISITION_TYPE]: [purchaseRequisitionRequired],
  [PO_CHANGE_TYPE]: [purchaseRequisitionRequired],
  [COST_QUOTATION_TYPE]: [costQuotationRequired],
  [SALES_CONTRACT_TYPE]: [salesContractRequired],
  [CREDIT_NOTE_TYPE]: [creditNoteRequired],
  [CLAIM_NOTE_TYPE]: [claimNoteRequired],
  [GROUP_SALES_MULTI_TYPE]: [groupSalesMultiRequired],
  // 레거시 순서 — 협조결재자(지정 시 소속 확인, 상신 시 1인) 다음 표·금액
  [SAP_BUDGET_INCREASE_TYPE]: [
    ...coApproverRestrictionChecks(SAP_BUDGET_INCREASE_TYPE),
    sapBudgetIncreaseRequired,
  ],
  // 레거시 순서 — 협조결재자(지정 시 소속 확인, 상신 시 1인) 다음 거래처 표·업체소싱정보
  [ACCOUNTING_BP_TYPE]: [
    ...coApproverRestrictionChecks(ACCOUNTING_BP_TYPE),
    accountingBpRequired,
  ],
  [SAP_COLLATERAL_CREATION_TYPE]: [sapCollateralChangeRequired],
  [SAP_COLLATERAL_CHANGE_TYPE]: [sapCollateralChangeRequired],
  [INFLUENCER_PPL_TYPE]: [influencerPplRequired],
  [INFLUENCER_MONTHLY_REPORT_TYPE]: [influencerMonthlyReportRequired],
  [PRODUCTION_COST_TYPE]: [productionCostRequired],
  [EXTERNAL_LOAN_TYPE]: [externalLoanRequired],
  [SAP_BOND_STATUS_TYPE]: [sapBondStatusRequired],
  [SA_TRAVEL_EXPENSE_TYPE]: [saTravelExpenseRequired],
  [SA_TRAVEL_REQUEST_TYPE]: [saTravelExpenseRequired],
  [FNCO_PRICE_SUPPLY_TYPE]: [fncoPriceSupplyRequired],
  [FNCO_PRICE_DISCOUNT_TYPE]: [fncoPriceSupplyRequired],
  [FNCO_PRICE_SALE_TYPE]: [fncoPriceSupplyRequired],
  [DISCOUNT_VOUCHER_TYPE]: [discountVoucherRequired],
  99: [saVoucherRequired, ...coApproverRestrictionChecks(99)],
};

/** 공통 → 유형별 순서로 첫 위반 문구만 반환 */
export const findSubmitViolation = (ctx: SubmitCheckContext): string | null => {
  const checks = [
    ...COMMON_SUBMIT_CHECKS,
    ...(TYPE_SUBMIT_CHECKS[ctx.apvType] ?? []),
  ];
  for (const check of checks) {
    const message = check(ctx);
    if (message) return message;
  }
  return null;
};
